// 공용 모달 store + Promise 기반 API.
//   await tptAlert('저장됨')                       → 확인 1버튼
//   const ok = await tptConfirm('삭제할까요?')     → boolean
//   const v  = await tptPrompt('이름', '기본값?')  → string | null
//   const v  = await tptPrompt('공지', 현재값, { multiline: true, rows: 6 })  → 여러 줄(textarea)
// ModalRoot 컴포넌트가 store.active 를 subscribe 하여 dialog 렌더.

import { create } from 'zustand';
import i18n from '../i18n';

export type ModalKind = 'alert' | 'confirm' | 'prompt';

export interface ActiveModal {
	kind: ModalKind;
	title?: string;
	message: string;
	defaultValue?: string;
	placeholder?: string;
	okLabel?: string;
	cancelLabel?: string;
	danger?: boolean;
	/** prompt 전용 — 여러 줄 입력(textarea). Enter 는 줄바꿈, Ctrl/⌘+Enter 가 확인. */
	multiline?: boolean;
	/** multiline 일 때 textarea 줄 수 (기본 5). */
	rows?: number;
	resolve: (value: unknown) => void;
}

interface State {
	active: ActiveModal | null;
	open: (m: Omit<ActiveModal, 'resolve'>) => Promise<unknown>;
	close: (value: unknown) => void;
}

export const useModalStore = create<State>((set, get) => ({
	active: null,
	open: (m) => new Promise((resolve) => {
		// 이미 열린 모달이 있으면 — 새 호출이 우선 (이전은 cancel 처리)
		const cur = get().active;
		if (cur) cur.resolve(undefined);
		set({ active: { ...m, resolve } });
	}),
	close: (value) => {
		const cur = get().active;
		if (cur) cur.resolve(value);
		set({ active: null });
	},
}));

/** 안내 모달 — 확인 1버튼. resolve undefined. */
export function tptAlert(message: string, opts?: { title?: string; okLabel?: string }): Promise<void> {
	return useModalStore.getState().open({ kind: 'alert', message, ...opts }).then(() => undefined);
}

/** 확인 모달 — 취소/확인 2버튼. resolve boolean. */
export function tptConfirm(message: string, opts?: { title?: string; okLabel?: string; cancelLabel?: string; danger?: boolean }): Promise<boolean> {
	return useModalStore.getState().open({ kind: 'confirm', message, ...opts }).then((v) => v === true);
}

/** 입력 모달 — 텍스트 input(기본) 또는 textarea(`multiline`). resolve string | null (취소 시 null). */
export function tptPrompt(message: string, defaultValue?: string, opts?: { title?: string; placeholder?: string; okLabel?: string; multiline?: boolean; rows?: number }): Promise<string | null> {
	return useModalStore.getState().open({ kind: 'prompt', message, defaultValue, ...opts }).then((v) => (v == null ? null : String(v)));
}

/**
 * raw 에러를 사용자 친화 한국어 메시지로 변환.
 *  - HTTP 401/403/404, 네트워크/타임아웃, 비밀번호 정책 등 흔한 패턴 핸들.
 *  - 매칭 실패 시 generic 메시지 + 짧은 원문(debug 힌트).
 * 사용: `void tptAlert(humanizeError(e))` 또는 `humanizeError(e, '메시지 전송 실패')`.
 */
// 🔤 RC 서버 errorType → 사용자 문구 키 (2026-08-12 전수 점검).
//   RC 는 실패 사유를 'error-xxx' 코드로 주는데, 그동안 이 원문이 그대로 말풍선/알럿에 노출됐다
//   (예: "chat.delete 400: Not allowed [error-action-not-allowed]", "User is not a moderator").
//   코드가 메시지 어디에 있든 뽑아내 사용자 문장으로 바꾼다.
const RC_ERROR_KEYS: Record<string, string> = {
	'error-action-not-allowed': 'dialog.rcActionNotAllowed',
	'error-not-allowed': 'dialog.rcActionNotAllowed',
	'error-not-authorized': 'dialog.err403',
	'error-unauthorized': 'dialog.err401',
	'error-invalid-token': 'dialog.err401',
	'error-room-not-found': 'dialog.rcRoomNotFound',
	'error-invalid-room': 'dialog.rcRoomNotFound',
	'error-room-archived': 'dialog.rcRoomArchived',
	'error-invalid-user': 'dialog.rcInvalidUser',
	'error-user-not-found': 'dialog.rcInvalidUser',
	'error-user-not-in-room': 'dialog.rcUserNotInRoom',
	'error-user-not-moderator': 'dialog.rcNotModerator',
	'error-user-not-owner': 'dialog.rcNotModerator',
	'error-remove-last-owner': 'dialog.rcRemoveLastOwner',
	'error-you-are-last-owner': 'dialog.rcLastOwnerLeave',
	'error-invalid-message': 'dialog.rcInvalidMessage',
	'error-message-deleting-blocked': 'dialog.rcDeleteBlocked',
	'error-message-editing-blocked': 'dialog.rcEditBlocked',
	'error-duplicate-channel-name': 'dialog.rcDuplicateName',
	'error-duplicate-channel': 'dialog.rcDuplicateName',
	'error-invalid-name': 'dialog.rcInvalidName',
	'error-invalid-room-name': 'dialog.rcInvalidName',
	'error-file-too-large': 'dialog.rcFileTooLarge',
	'error-invalid-file-type': 'dialog.rcInvalidFileType',
	'error-cant-invite-for-direct-room': 'dialog.rcCantInviteDirect',
	'error-user-is-not-activated': 'dialog.rcUserNotActivated',
	'error-user-registration-disabled': 'dialog.rcActionNotAllowed',
	'error-room-read-only': 'dialog.rcReadOnly',
	'error-user-is-muted': 'dialog.rcUserMuted',
	'error-empty-invite-list': 'dialog.rcInvalidUser',
	'error-max-rooms-per-user': 'dialog.rcActionNotAllowed',
	'error-too-many-requests': 'dialog.err429',
};

export function humanizeError(raw: unknown, action?: string): string {
	const msg = ((raw as Error)?.message ?? String(raw ?? '')).trim();
	const prefix = action ? `${action} — ` : '';
	if (!msg) return `${prefix}${i18n.t('dialog.errUnknown')}`;
	// ① RC 오류코드 — 메시지 어디에 있어도 우선 매칭 (가장 정확한 사유)
	const code = /(error-[a-z0-9-]+)/i.exec(msg)?.[1]?.toLowerCase();
	if (code && RC_ERROR_KEYS[code]) return `${prefix}${i18n.t(RC_ERROR_KEYS[code])}`;
	// ② 이미 사람이 읽을 수 있는 우리말/현지어 안내면 그대로 (사이드카가 주는 한국어 메시지 등)
	if (/[가-힣぀-ヿ一-鿿]/.test(msg) && msg.length <= 200 && !/^[a-z.]+\s+\d{3}:/i.test(msg)) {
		return `${prefix}${msg}`;
	}
	const low = msg.toLowerCase();
	if (/(^|[^\d])401([^\d]|$)|unauthor/i.test(msg)) return `${prefix}${i18n.t('dialog.err401')}`;
	if (/(^|[^\d])403([^\d]|$)|forbidden|not.?authoriz/i.test(msg)) return `${prefix}${i18n.t('dialog.err403')}`;
	if (/(^|[^\d])404([^\d]|$)|not.?found/i.test(msg)) return `${prefix}${i18n.t('dialog.err404')}`;
	if (/(^|[^\d])429([^\d]|$)|rate.?limit/i.test(msg)) return `${prefix}${i18n.t('dialog.err429')}`;
	if (/(^|[^\d])5\d\d([^\d]|$)|internal.?server/i.test(msg)) return `${prefix}${i18n.t('dialog.err5xx')}`;
	if (/timeout|timed.?out|ETIMED/i.test(msg)) return `${prefix}${i18n.t('dialog.errTimeout')}`;
	if (/network|failed to fetch|ECONNREFUSED|ENOTFOUND|net::/i.test(msg)) return `${prefix}${i18n.t('dialog.errNetwork')}`;
	if (/error-password-policy|password.?policy|does not meet.*password/i.test(msg)) return `${prefix}${i18n.t('dialog.errPasswordPolicy')}`;
	if (/current.?password|incorrect.?password|invalid.?password/i.test(msg)) return `${prefix}${i18n.t('dialog.errPasswordMismatch')}`;
	// 🔐 2FA — 코드 불일치(totp-required/invalid)만 err2fa. 그 밖의 2FA 에러는 뭉개지 말고 세분화(2026-08-27 등록 실패가 '코드 불일치' 로 오표기된 사고).
	if (/already.?exist|duplicate|already.?enabled/i.test(low)) return `${prefix}${i18n.t('dialog.errDuplicate')}`;
	if (/verify your emails|verify.*email.*before/i.test(low)) return `${prefix}${i18n.t('dialog.err2faSetupEmail')}`;
	if (/totp-invalid|totp-required|2fa.?code|invalid.?totp|코드/i.test(low)) return `${prefix}${i18n.t('dialog.err2fa')}`;
	if (/totp|2fa/i.test(low)) { console.warn('[2fa]', msg); return `${prefix}${i18n.t('dialog.err2faGeneric')}`; }
	if (/not a moderator|not an owner/i.test(low)) return `${prefix}${i18n.t('dialog.rcNotModerator')}`;
	if (/last owner/i.test(low)) return `${prefix}${i18n.t('dialog.rcRemoveLastOwner')}`;
	if (/is archived/i.test(low)) return `${prefix}${i18n.t('dialog.rcRoomArchived')}`;
	if (/read.?only/i.test(low)) return `${prefix}${i18n.t('dialog.rcReadOnly')}`;
	// ③ fallback — 영문 raw 는 사용자에게 보여주지 않는다(원문은 콘솔로만). "무엇을 하면 되는지" 안내.
	//   2026-08-12 사용자: "시스템 오류가 아니라 사용자가 인지할 수 있는 오류가 났으면 함".
	console.warn('[error]', msg);
	return `${prefix}${i18n.t('dialog.errRetry')}`;
}
